import { Injectable } from "@angular/core";
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from "@angular/router";
import { AzureSearchService, AzureSearchRequest, AzureSearchResult } from "@rubbl-azure-search";
import { LogService } from "@rubbl-core";
import { InventoryService, InventoryItem, Attachment } from "@rubbl-inventory";

import { EMPTY, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class MachineDetailsResolverService implements Resolve<any> {
  constructor(
    private azureSearchService: AzureSearchService,
    private inventoryService: InventoryService,
    private router: Router,
    private logService: LogService,
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Observable<never> {
    const id = route.queryParamMap.get("id");
    if (!id) {
      // go to search results
      this.logService.info(this.constructor.name, "Machine Id query string is null, rerouting to search results.");
      this.router.navigate(["/equipment-rentals"]);
      return EMPTY;
    }

    return this.azureSearchService.getOne<InventoryItem>(id, "machines").pipe(
      switchMap((inventoryItem: InventoryItem) => {
        if (inventoryItem.images.length == 0) {
          inventoryItem.images = ["/pubweb/marketplace/images/images/machine-no-photos.png"];
        }
        // filter for available attahcments and get unique attachments by displayname
        inventoryItem.relatedAttachments = inventoryItem.relatedAttachments
          .filter(d => d.status === "Available" || d.status === "Onboarding")
          .filter((value, index, self) => index === self.findIndex(t => t.displayName === value.displayName));

        // grab attachment ids to get from attachment index
        const attachmentIds = inventoryItem.relatedAttachments.map(d => d.inventoryItemId);
        // bail if we do not have any attachments
        if (attachmentIds.length === 0) {
          return of(inventoryItem);
        }

        const attachmentRequest = new AzureSearchRequest();
        //  and status eq 'Available' // might not need this since we need to query for all attachments for validation
        // but also might be able to use this to compare the related attachments
        attachmentRequest.filter = `search.in(id, '${attachmentIds}')`;
        return this.azureSearchService.search<Attachment>(attachmentRequest, "attachments").pipe(
          map((result: AzureSearchResult<Attachment>) => {
            // lets set related items to the latest values from the attachments index
            for (let attachment of inventoryItem.relatedAttachments) {
              const attachmentFromIndex = result.value.find(d => d.id === attachment.inventoryItemId);
              if (!attachmentFromIndex) {
                continue;
              }

              if (attachmentFromIndex.specifications?.length) {
                attachment.specifications = attachmentFromIndex.specifications;
                attachment.description = this.inventoryService.getFormattedSpecifications(attachmentFromIndex.specifications);
              }

              attachment.compatibleWith = attachmentFromIndex.compatibleWith;
              attachment.accountId = attachmentFromIndex.accountId;
              attachment.childItemId = attachment.inventoryItemId; // cart item uses child item id
              attachment.price = attachment.cost; // cart item uses price
            }
            return inventoryItem;
          }),
        );
      }),
      map(inventoryItem => {
        return {
          ...inventoryItem,
          ratingInfo: {
            overall: inventoryItem.rating,
          },
        };
      }),
    );
  }
}
